import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../Layout';
import SegmentedControl from '../gdk/SegmentedControl/SegmentedControl';
import { GdkToasts } from '@gdk/toasts';
import { UserContext } from '../../context/UserContextProvider';
import { searchAgencies } from '../../api/search';
import { createOrRetrieveBindingAuthorityList } from '../../api/admin';
import { producerOverride } from '../../api/licensing';
import { retrieveIdentityInfo } from '../../api/identity';
import DesignatedLicensedProducers from './DesignatedLicensedProducers';
import CsrSupervisorAssignments from './CsrSupervisorAssignments';
import DiscardChangesModal from './DiscardChangesModal';

export default function ManageBindingAuthority() {
    const navigate = useNavigate();
    const user = useContext(UserContext);
    const [agencyInfo, setAgencyInfo] = useState(null);
    const [activeSegment, setActiveSegment] = useState({ innerText: 'Designated Licensed Producers' });
    const [activeIndex, setActiveIndex] = useState(null);
    const [isSaveEnabled, setIsSaveEnabled] = useState(false);

    // Shared producers state (read by both segments, written by both)
    const [allApiProducers, setAllApiProducers] = useState([]);
    const [designatedProducers, setDesignatedProducers] = useState([]);
    const [principalId, setPrincipalId] = useState(null);
    const [principalName, setPrincipalName] = useState('');

    // Shared refs
    const isDirtyRef = useRef(false);
    const savedProducersRef = useRef([]);
    const isOnListRef = useRef(false);
    const listRecordIdRef = useRef(null);

    // Toast refs (DOM elements live in this component)
    const saveSuccessToastRef = useRef(null);
    const csrAssignSuccessToastRef = useRef(null);

    // Error states
    const [showSaveError, setShowSaveError] = useState(false);
    const [showCsrAssignError, setShowCsrAssignError] = useState(false);

    // Discard modal ref (exposes .open(action, segmentIndex) via forwardRef)
    const discardModalRef = useRef();

    // --- API: retrieve existing list ---
    const onRetrieveIdentitySuccess = (response) => {
        const principal = response?.ApplicationExtensionData?.AgencyProfile?.principal;
        const id = principal?.id;
        if (!id) return;
        setPrincipalId(id);
        const firstName = principal?.name?.firstName ?? '';
        const lastName = principal?.name?.lastName ?? '';
        setPrincipalName(`${firstName} ${lastName}`.trim());
        setDesignatedProducers(prev => {
            const updated = prev.map(p =>
                p.agentId === id ? { ...p, isDefault: true } : p
            );
            return [...updated].sort(
                (a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0)
            );
        });
    };

    const getIdentityInfo = () => {
        retrieveIdentityInfo(
            { id: user.agencyId },
            onRetrieveIdentitySuccess,
            (error) => { console.error(error); }
        );
    };

    const fetchList = () => {
        if (!user?.agencyId) return;
        createOrRetrieveBindingAuthorityList(
            (response) => {
                if (response && response.domain?.length > 0) {
                    isOnListRef.current = true;
                    listRecordIdRef.current = response.domain[0].id;
                    const agents = response.domain[0].applicationExtensionData?.AdminList?.bindingAuthority?.agents || [];
                    setDesignatedProducers(agents);
                    getIdentityInfo();
                } else {
                    isOnListRef.current = false;
                    listRecordIdRef.current = null;
                    setDesignatedProducers([]);
                    getIdentityInfo();
                }
            },
            () => { }
        );
    };

    useEffect(() => {
        fetchList();
    }, [user?.agencyId]);

    // --- API: producer override list ---
    useEffect(() => {
        if (!user?.agencyId) return;
        producerOverride(
            (response) => { setAllApiProducers(response?.domain?.agents || []); },
            (error) => { console.error(error); }
        );
    }, [user?.agencyId]);

    // --- API: agency info ---
    useEffect(() => {
        if (!user?.agencyId) {
            setAgencyInfo(null);
            return;
        }
        searchAgencies({ agencyId: user.agencyId }, (response) => {
            const agency = response?.find(a => a.agencyId === user.agencyId);
            if (agency) {
                setAgencyInfo({
                    agencyId: user.agencyId,
                    agentId: user.agentId,
                    name: agency.name,
                    npn: agency.agencyNationalProducerNumber,
                    fein: agency.federalEmployerIdentificationNumber,
                });
            }
        });
    }, [user?.agencyId]);

    // Keep savedProducersRef in sync whenever designatedProducers changes outside of a
    // dirty DLP edit (e.g. after CSR assignment saves). This prevents discarding DLP
    // changes from reverting in-memory CSR assignments to a stale snapshot.
    useEffect(() => {
        if (!isDirtyRef.current) {
            savedProducersRef.current = designatedProducers;
        }
    }, [designatedProducers]);

    // --- Initialise GDK toasts ---
    useEffect(() => {
        saveSuccessToastRef.current = new GdkToasts({
            content: '#binding-authority-success-toast',
            floatTime: 'short',
            closeBtn: true,
            baseTimer: true,
        });
        csrAssignSuccessToastRef.current = new GdkToasts({
            content: '#csr-assign-success-toast',
            floatTime: 'short',
            closeBtn: true,
            baseTimer: true,
        });
    }, []);

    // --- Navigation / segment guards ---
    const openDiscardModal = (action, segmentIndex = null) => {
        discardModalRef.current.open(action, segmentIndex);
    };

    const handleNavAttempt = (action) => {
        if (isDirtyRef.current) {
            openDiscardModal(action);
        } else {
            action();
        }
    };

    const handleSegmentAttempt = (segment) => {
        if (
            isDirtyRef.current &&
            activeSegment?.innerText === 'Designated Licensed Producers' &&
            segment?.innerText === 'CSR Supervisor Assignments'
        ) {
            setActiveIndex(1); // revert GDK tab visually while modal is open
            openDiscardModal(() => setActiveSegment(segment), 2);
        } else {
            setActiveSegment(segment);
        }
    };

    const handleDiscardAndLeave = (action, segmentIndex) => {
        isDirtyRef.current = false;
        setIsSaveEnabled(false);
        if (segmentIndex !== null) {
            setDesignatedProducers(savedProducersRef.current);
            setActiveIndex(segmentIndex);
        }
        action?.();
    };

    return (
        <Layout>
            <div className="page-header--wrapper">
                <div className="container">
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="page-header" style={{ marginBottom: '0px' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <h2 style={{ marginBottom: '0.5rem' }}>Manage Employee Binding Authority</h2>
                                    <p>Add or remove designated licensed producers and manage/supervise customer service representative assignments.</p>
                                </div>
                            </div>
                            <div style={{ width: '100%', textAlign: 'center', paddingBottom: '20px' }}>
                                <gds-button appearance="secondary" type="button" size="medium" onClick={() => handleNavAttempt(() => navigate(-1))}>
                                    Back
                                </gds-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container margin-top">
                {/* Agency info */}
                <div className="row">
                    <div className="col-sm-12" style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <h4 style={{ fontWeight: '400' }}>
                            Agency: {agencyInfo?.name ?? ''} | Agency ID: {user?.agencyId ?? ''}
                        </h4>
                    </div>
                </div>

                <SegmentedControl
                    segment1="Designated Licensed Producers"
                    segment2="CSR Supervisor Assignments"
                    activeIndex={activeIndex}
                    setActiveSegment={handleSegmentAttempt}
                />

                {activeSegment?.innerText === 'Designated Licensed Producers' && (
                    <div className="row margin-top">
                        <div className="col-sm-12">
                            <DesignatedLicensedProducers
                                fetchList={fetchList}
                                principalId={principalId}
                                designatedProducers={designatedProducers}
                                setDesignatedProducers={setDesignatedProducers}
                                allApiProducers={allApiProducers}
                                agencyInfo={agencyInfo}
                                isOnListRef={isOnListRef}
                                listRecordIdRef={listRecordIdRef}
                                isSaveEnabled={isSaveEnabled}
                                setIsSaveEnabled={setIsSaveEnabled}
                                isDirtyRef={isDirtyRef}
                                savedProducersRef={savedProducersRef}
                                showSaveError={showSaveError}
                                setShowSaveError={setShowSaveError}
                                saveSuccessToastRef={saveSuccessToastRef}
                                onNavigateBack={() => handleNavAttempt(() => navigate(-1))}
                            />
                        </div>
                    </div>
                )}

                {activeSegment?.innerText === 'CSR Supervisor Assignments' && (
                    <div className="row margin-top">
                        <div className="col-sm-12">
                            <CsrSupervisorAssignments
                                fetchList={fetchList}
                                principalId={principalId}
                                principalName={principalName}
                                designatedProducers={designatedProducers}
                                setDesignatedProducers={setDesignatedProducers}
                                allApiProducers={allApiProducers}
                                agencyInfo={agencyInfo}
                                isOnListRef={isOnListRef}
                                listRecordIdRef={listRecordIdRef}
                                showCsrAssignError={showCsrAssignError}
                                setShowCsrAssignError={setShowCsrAssignError}
                                csrAssignSuccessToastRef={csrAssignSuccessToastRef}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Toast containers */}
            <div id="binding-authority-success-toast" className="toasts-container">
                <div className="toasts toasts--confirmation">
                    <div><span className="icon-confirmation" /></div>
                    <output>
                        <div className="toasts--heading">Changes Saved Successfully!</div>
                        <div className="toasts--content">Selected designated licensed producers are now available to customer service representatives during the binding process.</div>
                    </output>
                    <button className="toasts--close-btn icon-close">
                        <div id="toasts--base-timer" />
                    </button>
                </div>
            </div>

            <div id="csr-assign-success-toast" className="toasts-container">
                <div className="toasts toasts--confirmation">
                    <div><span className="icon-confirmation" /></div>
                    <output>
                        <div className="toasts--heading">Success!</div>
                        <div className="toasts--content">Licensed agent has been designated.</div>
                    </output>
                    <button className="toasts--close-btn icon-close">
                        <div id="toasts--base-timer" />
                    </button>
                </div>
            </div>

            <DiscardChangesModal
                ref={discardModalRef}
                onDiscardAndLeave={handleDiscardAndLeave}
            />
        </Layout>
    );
}


